<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import { OrderFreightType, OrderStopType, OrderStopTypeMap } from "@/constants/order";
import { isSameEntity } from "@/utils/sameEntity";
import type { TripRouteFreight, TripRouteStop } from "@/types/trip";

const model = defineModel<boolean>({ required: true });

const props = defineProps<{
  stop: TripRouteStop;
  stopFreights: TripRouteFreight[];
  routeFreights: TripRouteFreight[];
}>();

const emit = defineEmits<{
  (e: "confirm"): void;
}>();

const { t } = useI18n();

const isUndo = computed(() => {
  return Boolean(props.stop.isDeleted);
});

const stopTypeLabel = computed(() => {
  return OrderStopTypeMap[props.stop.type];
});

const title = computed(() => {
  const key = isUndo.value ? "trip.undo-delete-stop-dialog-title" : "trip.delete-stop-dialog-title";

  return t(key, { type: stopTypeLabel.value, number: props.stop.number });
});

const primaryActionLabel = computed(() => {
  return isUndo.value ? "Undo delete" : "Delete";
});

const isPickup = computed(() => {
  return props.stop.type === OrderStopType.pickup;
});

const affectedFreights = computed(() => {
  return props.stopFreights
    .filter((freight) => !freight.isDeleted)
    .map((freight) => {
      const sameFreights = props.routeFreights.filter((rf) => rf.freightId === freight.freightId);
      const index = sameFreights.findIndex((rf) => isSameEntity(rf, freight));

      return {
        number: index === -1 ? (freight.number ?? 0) : index + 1,
        details: getFreightDetails(freight),
      };
    });
});

const freightsMessage = computed(() => {
  const freights = affectedFreights.value
    .map((freight) => t("trip.route-freight-item", { number: freight.number, details: freight.details }))
    .join(", ");

  const key = isPickup.value ? "trip.route-freights-picked-up-reassign" : "trip.route-freights-delivered-reassign";

  return t(key, { freights });
});

function getFreightDetails(freight: TripRouteFreight): string {
  const parts: string[] = [];
  const quantity = freight.quantity ?? freight.plannedQuantity;

  if (quantity) {
    parts.push(`${quantity} ${freight.type === OrderFreightType.pallets ? "pallets" : "pieces"}`);
  }

  const weight = freight.weight ?? freight.plannedWeight;

  if (weight) {
    parts.push(`${weight} lbs`);
  }

  return parts.join(", ");
}

function confirm() {
  emit("confirm");
  model.value = false;
}
</script>

<template>
  <omni-dialog
    v-model="model"
    :primary-action-label="primaryActionLabel"
    :title="title"
    secondary-action-label="Cancel"
    width="600"
    @primary-action:click="confirm"
    @secondary-action:click="model = false"
  >
    <template v-if="isUndo">
      <div class="text-grey-darken-1">
        {{ t("trip.undo-delete-stop-message", { type: stopTypeLabel, number: stop.number }) }}
      </div>
    </template>
    <template v-else-if="!affectedFreights.length">
      <div class="text-grey-darken-1">
        {{ t("trip.delete-stop-no-freights-message", { type: stopTypeLabel, number: stop.number }) }}
      </div>
    </template>
    <template v-else>
      <div class="text-grey-darken-1">
        {{ freightsMessage }}
      </div>
    </template>
  </omni-dialog>
</template>
